import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as Styled from './Treemap.styles';
import * as d3 from 'd3';
import { addEllipsisOverflow } from './utils';
import {
  useFloating,
  shift,
  flip,
  offset,
  useClientPoint,
  useInteractions,
} from '@floating-ui/react';

export type TreeNode<T = unknown> = {
  id?: string;
  value: number;
  name: string;
  children?: TreeNode<T>[];
  weight: number;
  options?: {
    centered?: boolean;
    reverseEllipsis?: boolean;
    filler?: boolean;
  };
  data?: T;
};

export interface TreemapProps<T> {
  width: number;
  height: number;
  data: TreeNode<T>;
  LeafTooltipComponent?: React.FC<{ leaf: TreeNode<T> }>;
  onClick?: (node: TreeNode<T>) => void;
}

const GROUP_TEXT_HEIGHT = 26;
const GROUP_TEXT_PADDING = 6;
const LEAF_TEXT_PADDING = 6;

export function Treemap<T>({
  width,
  height,
  data,
  onClick,
  LeafTooltipComponent = DefaultLeafTooltip,
}: TreemapProps<T>) {
  const ref = useRef<SVGSVGElement>(null);
  const { refs, floatingStyles, update, context } = useFloating({
    placement: 'top',
    middleware: [offset(10), flip(), shift()],
  });

  const clientPoint = useClientPoint(context);

  const { getFloatingProps } = useInteractions([clientPoint]);

  const [currentLeaf, setCurrentLeaf] = useState<TreeNode<T> | undefined>(
    undefined,
  );

  const hierarchy: d3.HierarchyNode<TreeNode<T>> | undefined = useMemo(() => {
    return d3.hierarchy(data).sum((d) => d.value);
  }, [data]);

  const root = useMemo(() => {
    const treeGenerator = d3
      .treemap<TreeNode<T>>()
      .size([width, height])
      .paddingInner(3)
      .paddingOuter(8)
      .paddingTop(GROUP_TEXT_HEIGHT);
    return treeGenerator(hierarchy);
  }, [hierarchy, width, height]);

  function handleMouseOver(
    event: React.MouseEvent<SVGElement>,
    leaf: d3.HierarchyRectangularNode<TreeNode<T>>,
  ) {
    if (!LeafTooltipComponent) return;

    if (currentLeaf !== leaf.data) {
      setCurrentLeaf(leaf.data);
    }

    refs.setReference(event.currentTarget);
    update();
  }

  function handleMouseOut() {
    setCurrentLeaf(undefined);
  }

  const groupShapes = root.children?.map((child) => {
    const childWidth = child.x1 - child.x0;
    const childHeight = child.y1 - child.y0;
    const showText = shouldShowText(childWidth, childHeight);

    return (
      <g key={`child-${child.data.id ?? child.data.name}`}>
        <Styled.GroupRect
          x={child.x0}
          y={child.y0}
          width={childWidth}
          height={childHeight}
          rx={2}
          weight={child.data.weight}
          onMouseMove={
            showText
              ? undefined
              : (event: React.MouseEvent<SVGRectElement>) =>
                  handleMouseOver(event, child)
          }
          onMouseOut={showText ? undefined : handleMouseOut}
          aria-label={`group: ${child.data.name} weight: ${child.data.weight} value: ${child.data.value}`}
        />
        {showText && (
          <Styled.GroupText
            x={child.x0 + GROUP_TEXT_PADDING}
            y={child.y0 + 8}
            width={childWidth - GROUP_TEXT_PADDING * 2}
            fontSize={10}
            alignmentBaseline="hanging"
            weight={child.data.weight}
            data-original-text={child.data.name}
            data-reverse-ellipsis={child.data.options?.reverseEllipsis}
            onMouseMove={(event: React.MouseEvent<SVGTextElement>) =>
              handleMouseOver(event, child)
            }
            onMouseOut={handleMouseOut}
            aria-label={`group: ${child.data.name} weight: ${child.data.weight} value: ${child.data.value}`}
          >
            {child.data.name}
          </Styled.GroupText>
        )}
      </g>
    );
  });

  const allShapes = root.leaves().map((node) => {
    const leafWidth = node.x1 - node.x0 + 1;
    const leafHeight = node.y1 - node.y0 + 1;

    const showText = shouldShowText(leafWidth, leafHeight);

    if (node.depth === 1) {
      return <></>;
    }

    const leaf = node.data;

    const textX = leaf.options?.centered
      ? node.x0 + leafWidth / 2
      : node.x0 + LEAF_TEXT_PADDING;
    const textY = leaf.options?.centered
      ? node.y0 + leafHeight / 2
      : node.y0 + 10;

    return (
      <g key={`leaf-${leaf.id ?? leaf.name}`}>
        {!leaf.options?.filler ? (
          <>
            <Styled.LeafBackgroundRect
              x={node.x0}
              y={node.y0}
              width={leafWidth}
              height={leafHeight}
              weight={leaf.weight}
            />

            <Styled.LeafRect
              aria-label={`name:${leaf.name}; weight: ${leaf.weight}; value: ${leaf.value}`}
              x={node.x0}
              y={node.y0}
              width={leafWidth}
              height={leafHeight}
              weight={leaf.weight}
              onMouseMove={(event: React.MouseEvent<SVGRectElement>) =>
                handleMouseOver(event, node)
              }
              onMouseOut={handleMouseOut}
              onClick={() => {
                if (onClick) {
                  onClick(leaf);
                }
              }}
            />
          </>
        ) : null}
        {showText && (
          <Styled.LeafText
            x={textX}
            y={textY}
            width={leafWidth - 2 * LEAF_TEXT_PADDING}
            fontSize={10}
            alignmentBaseline="hanging"
            weight={leaf.weight}
            data-original-text={leaf.name}
            centered={leaf.options?.centered}
          >
            {leaf.name}
          </Styled.LeafText>
        )}
      </g>
    );
  });

  useEffect(() => {
    if (!ref.current) return;

    const svg = d3.select(ref.current);
    svg.selectAll<SVGTextElement, unknown>('text').call(addEllipsisOverflow);
  }, [width]);

  return (
    <Styled.Container>
      <Styled.Tooltip
        ref={refs.setFloating}
        style={{ ...floatingStyles, display: currentLeaf ? 'block' : 'none' }}
        {...getFloatingProps()}
      >
        {currentLeaf && LeafTooltipComponent && (
          <LeafTooltipComponent leaf={currentLeaf} />
        )}
      </Styled.Tooltip>

      <svg
        width={width}
        height={height}
        viewBox={`0 ${GROUP_TEXT_HEIGHT - 8} ${width} ${height}`}
        ref={ref}
      >
        {groupShapes}
        {allShapes}
      </svg>
    </Styled.Container>
  );
}

function DefaultLeafTooltip<T>({ leaf }: { leaf: TreeNode<T> }) {
  return <Styled.DefaultTooltip>{leaf.name}</Styled.DefaultTooltip>;
}

function shouldShowText(width: number, height: number) {
  return width > 40 && height >= 20;
}
